import type { BlockHeadingEffect } from "@newxsystems/types";
import type { ComponentType } from "react";

/**
 * Heading styles a block can mount. The list mirrors `HEADING_EFFECT_OPTIONS` in
 * `lib/keystatic/src/fields.ts` one-for-one: a value the CMS offers with no loader here
 * would fall back to plain text, which is what the fallback is for, but the two lists
 * are meant to be edited together.
 */
export const TEXT_EFFECTS = [
  "warp-text",
  "stroke-text",
  "fold-text",
  "split-text",
  "blur-text",
  "text-type",
  "shiny-text",
  "scroll-float",
  "scroll-reveal",
] as const;

/** The CMS-facing type is the shared one, so every block speaks the same value set. */
export type TextEffect = BlockHeadingEffect;

/**
 * Every style gets the same two things: the heading string, and whether it is running.
 * `active` is false while the heading is warm but off screen, so an entrance animation
 * is still on its first frame when the reader arrives — and loops hold still instead of
 * animating for a screen nobody is looking at.
 */
export type TextEffectProps = {
  text: string;
  active: boolean;
};

/**
 * One lazy loader per style, so a page that picks `glitch-text` never downloads the
 * three canvas styles. Each file exports its component as the default export.
 */
export const TEXT_EFFECT_LOADERS: Record<
  TextEffect,
  () => Promise<{ default: ComponentType<TextEffectProps> }>
> = {
  "warp-text": () => import("./WarpText.client"),
  "stroke-text": () => import("./StrokeText.client"),
  "fold-text": () => import("./FoldText.client"),
  "split-text": () => import("./SplitText.client"),
  "blur-text": () => import("./BlurText.client"),
  "text-type": () => import("./TextType.client"),
  "shiny-text": () => import("./ShinyText.client"),
  "scroll-float": () => import("./ScrollFloat.client"),
  "scroll-reveal": () => import("./ScrollReveal.client"),
};

export const isTextEffect = (value: string | null | undefined): value is TextEffect =>
  Boolean(value) && (TEXT_EFFECTS as readonly string[]).includes(value as string);
